import React, { SyntheticEvent, useContext, useState } from 'react';
import styled from 'styled-components/macro';
import { translations } from '../../../../locales/translations';
import { useTranslation } from 'react-i18next';
import { DataGrid, GridApiContext, GridRowId, GridRowParams, GridSortModel, GridToolbarContainer, MuiEvent, selectedGridRowsSelector, visibleSortedGridRowsSelector, useGridSelector, gridSelectionStateSelector } from '@mui/x-data-grid';
import AssignmentLateOutlinedIcon from '@mui/icons-material/AssignmentLateOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import DoneOutlinedIcon from '@mui/icons-material/DoneOutlined';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FlagIcon from '@mui/icons-material/Flag';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import BlockIcon from '@mui/icons-material/Block'
import { Link } from '../../../components/Link';
import { commonStyle } from '../../../../styles/theme/themes';
import { biStatus } from '../../../../locales/types';
import { selectAccessRight, selectUsername } from '../slice/selectors';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import Popover from '@mui/material/Popover';
import { Button, Tooltip, Typography } from '@mui/material';
import AcknowledgeDialog from './AcknowledgeDialog';
import { UseAxios } from 'utils/UseAxios';
import { apiList } from 'locales/apiList';
import { useWorkListStateSlice, fetchWorkList } from '../slice';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import { toPathSegment } from '../../../../utils/contractNumber';

type CustomToolbarProps = {
  selectionModel: GridRowId[];
  handleAcknowledge: () => void;
}

const CustomToolbar: React.FC<CustomToolbarProps> = ({selectionModel, handleAcknowledge}) => {
  const gridApi = useContext(GridApiContext);
  const selectedRows = useGridSelector(gridApi, selectedGridRowsSelector);
  
  const isAllSelectedOpenContractAndStatusHavePending = Array.from(selectedRows?.values() ?? []).every(
    row => row.contractType === 'OpenContract' && 
    Object.keys(row).filter(key => key.endsWith('Status')).some(key => row[key] === biStatus.PENDING)
  );
  const isButtonDisabled = !isAllSelectedOpenContractAndStatusHavePending || selectionModel.length === 0;
  const button = (
    <Button
          variant="contained"
          color="primary"
          disabled={isButtonDisabled}
          onClick={handleAcknowledge}>Acknowledge Selected Contracts</Button>
  )
  return (
    <GridToolbarContainer>
      {isButtonDisabled ? (
        <Tooltip placement='right' title="Only available for Pending Open Contracts"><span>{button}</span></Tooltip>
      ) : (
        button
      )}
    </GridToolbarContainer>
  );
}

export function WorkListTable(props) {
  const { t } = useTranslation();
  const accessright = useSelector(selectAccessRight);
  const [pageSize, setPageSize] = useState(10);
  const rows = props.workListItems;
  const [sortModel, setSortModel] = React.useState<GridSortModel>([
    {
      field: 'biSubmittedDate',
      sort: 'desc',
    },
  ]);

  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [popoverRemark, setPopoverRemark] = React.useState<string | null>(null);
  const popoverOpen = Boolean(anchorEl);
  const [selectionModel, setSelectionModel] = React.useState<GridRowId[]>([]);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [isAcknowledging, setIsAcknowledging] = React.useState(false);
  const username = useSelector(selectUsername);
  const dispatch = useDispatch();

  const onSelectionModelChange = (newSelectionModel: GridRowId[]) => {
    setSelectionModel(newSelectionModel);
  };

  const handlePopoverOpen = (params: GridRowParams, event: MuiEvent<SyntheticEvent>) => {
    const eventTarget = event.target;
    if (params.row.remark) {
      setPopoverRemark(params.row.remark);
      if (eventTarget instanceof HTMLElement) {
        setAnchorEl(eventTarget);
      }
    } else {
      setAnchorEl(null);
      setPopoverRemark(null);
    }
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handleAcknowledge = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleDialogAcknowledge = async () => {
    setDialogOpen(false);
    setIsAcknowledging(true);
    const body = {
      contractNumbers: selectionModel,
      user: username,
    };
    // placeholder wait 5 sec
    // await new Promise(resolve => setTimeout(resolve, 5000));
    await UseAxios(`${apiList.putWorklistBatchStatusURL}`, 'put', body);
    setIsAcknowledging(false);
    dispatch(fetchWorkList({}));
  };

  const columnsData = [
    {
      field: 'contractNumber',
      headerName: t(translations.contractNo),
      sortable: true,
      disableColumnMenu: true,
      width: 135,
      renderCell: params => {
        return (
          <div className='cell'>
            <Link className='oacl-hyper' to={`${process.env.PUBLIC_URL}/detail/${toPathSegment(params.row.contractNumber)}`} target='_blank'>
              {params.row.contractNumber}
            </Link>
            { params.row['biStatus'] === biStatus.VOIDED && <span style={{color: 'red'}}><BlockIcon />VOID</span> }
          </div>
        );
      },
    },
    {
      field: 'biSubmittedDate',
      headerName: t(translations.contractDetail.contractInformation.biCreateDate),
      sortable: true,
      disableColumnMenu: true,
      width: 160,
      renderCell: params => {
        return returnDateElement(params, 'biSubmittedDate');
      },
    },
    {
      field: 'customerCode',
      headerName: t(translations.contractDetail.contractInformation.customerCode),
      sortable: true,
      disableColumnMenu: true,
      width: 130,
    },
    {
      field: 'contractType',
      headerName: t(translations.contractType),
      sortable: true,
      disableColumnMenu: true,
      width: 120,
      renderCell: params => {
        const contractType = params.row.contractType;
        return (
          <div>
            {contractType === 'OpenContract' ? 'Open Contract' : contractType}
          </div>
        );
      },
    },
    {
      field: 'signedDate',
      headerName: t(translations.contractDetail.contractInformation.signedDate),
      sortable: true,
      disableColumnMenu: true,
      width: 110,
    },
    {
      field: 'submittedBy',
      headerName: t(translations.contractDetail.contractInformation.submittedBy),
      sortable: true,
      disableColumnMenu: true,
      width: 140,
    },
    {
      field: 'contractInfoStatus',
      headerName: t(translations.biType.contractInfo),
      sortable: true,
      disableColumnMenu: true,
      width: 130,
      hide: !accessright.edit,
      renderCell: params => {
        return returnStatusElement(params, 'contractInfoStatus');
      }
    },
    {
      field: 'recurringStatus',
      headerName: t(translations.biType.recurring),
      sortable: true,
      disableColumnMenu: true,
      width: 110,
      hide: !accessright.edit,
      renderCell: params => {
        return returnStatusElement(params, 'recurringStatus');
      },
    },
    {
      field: 'oneOffStatus',
      headerName: t(translations.biType.oneOff),
      sortable: true,
      disableColumnMenu: true,
      width: 110,
      hide: !accessright.edit,
      renderCell: params => {
        return returnStatusElement(params, 'oneOffStatus');
      },
    },
    {
      field: 'depositStatus',
      headerName: t(translations.biType.deposit),
      sortable: true,
      disableColumnMenu: true,
      width: 110,
      hide: !accessright.edit,
      renderCell: params => {
        return returnStatusElement(params, 'depositStatus');
      },
    },
    {
      field: 'changeBillDateStatus',
      headerName: t(translations.biType.changeBillDate),
      sortable: true,
      disableColumnMenu: true,
      width: 140,
      hide: !accessright.edit,
      renderCell: params => {
        return returnStatusElement(params, 'changeBillDateStatus');
      },
    },
    {
      field: 'terminationStatus',
      headerName: t(translations.biType.termination),
      sortable: true,
      disableColumnMenu: true,
      width: 120,
      hide: !accessright.edit,
      renderCell: params => {
        return returnStatusElement(params, 'terminationStatus');
      },
    },
    {
      field: 'remark',
      headerName: t(translations.remark),
      sortable: true,
      disableColumnMenu: true,
      width: 80,
      hide: !accessright.edit,
      renderCell: params => {
        return returnRemarkElement(params, 'remark');
      },
    },
  ];
  const [columns, setColumns] = useState(columnsData);

  React.useEffect(() => {
    setColumns(columnsData);
  }, [accessright]);

  return (
    <Wrapper>
      <div className='worklist-header'><FormatListBulletedIcon />{t(translations.worklist)}</div>
      <DataGrid
        rows={rows}
        getRowId={row => row.contractNumber}
        columns={columns}
        sortModel={sortModel}
        onSortModelChange={(model) => setSortModel(model)}
        pageSize={pageSize}
        onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
        rowsPerPageOptions={[10, 20, 30]}
        // disableSelectionOnClick
        className='data-grid'
        autoHeight={true}
        onRowEnter={handlePopoverOpen}
        onRowLeave={handlePopoverClose}
        disableSelectionOnClick={!accessright.edit}
        checkboxSelection={accessright.edit}
        selectionModel={selectionModel}
        onSelectionModelChange={onSelectionModelChange}
        components={{
          Toolbar: accessright.edit ? () => <CustomToolbar selectionModel={selectionModel} handleAcknowledge={handleAcknowledge} /> : undefined,
        }}
      />
      <Popover
      sx={{pointerEvents:'none'}}
      open={popoverOpen && !!popoverRemark}
      anchorEl={anchorEl}
      anchorOrigin={{vertical:'bottom', horizontal: 'left'}}
      transformOrigin={{vertical: 'top', horizontal: 'left'}}
      onClose={handlePopoverClose}
      disableRestoreFocus
      >
        <Typography sx={{padding: 1}}><strong>Remark: </strong>{popoverRemark}</Typography>
      </Popover>
      <AcknowledgeDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        selectedContracts={selectionModel as string[]}
        onAcknowledge={handleDialogAcknowledge}
      />
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isAcknowledging}
      >
        <CircularProgress color="inherit" />
        <Typography sx={{ ml: 2 }}>Batch acknowledging...</Typography>
      </Backdrop>
    </Wrapper>
  );
}

const returnStatusElement = (params, statusType) => {
  const reason = params.row.statusReasons?.[statusType.replace('Status', '')];
  const content = (
    <div className={`worklist-row ${params.row[statusType] === biStatus.PENDING && 'pending-container'}`}>
      {params.row[statusType] === biStatus.PENDING && <AssignmentLateOutlinedIcon />}
      {params.row[statusType] === biStatus.COMPLETE && <AssignmentTurnedInOutlinedIcon />}
      {params.row[statusType] === biStatus.TBC && <PendingActionsIcon />}
      {params.row[statusType] === biStatus.AUTOComplete && <DoneOutlinedIcon />}
      {params.row[statusType] === biStatus.NA && <NotInterestedIcon />}
      {params.row[statusType] !== biStatus.NA && params.row[statusType]}
    </div>
  );
  return (reason && params.row[statusType] === biStatus.AUTOComplete)
    ? <Tooltip title={reason}>{content}</Tooltip> : content;
};

const returnDateElement = (params, columnName) => {
  return (
    <div>
      {moment(params.row[columnName]).format('YYYY-MM-DD HH:mm:ss')}
    </div>
  );
};

const returnRemarkElement = (params, columnName) => {
  return (
    <div>
      {params.row[columnName] && <FlagIcon />}
    </div>
  );
};

const Wrapper = styled.div`
  width: 100%;
  box-shadow: ${commonStyle.boxShadow};
  border-radius: 5px;

  .worklist-header {
    display: flex;
    align-items: center;
    padding: 10px;
    border-radius: 5px;
    background-color: ${p => p.theme.primary};
    color: ${p => p.theme.background};
    font-size: 16px;
  }

  .MuiTableCell-root {
    line-height: 15px;
    border-right: 1px solid rgba(224, 224, 224, 1) !important;
  }

  .oacl-hyper {
    color: ${p => p.theme.buttonColor};
    text-decoration: underline;
  }

  .cell {
    display: flex;
    align-items: center;
  }

  .worklist-row {
    display: flex;
    align-items: center;
  }

  .pending-container {
    color: ${p => p.theme.pendingWorkList};
  }

  .MuiDataGrid-root .MuiDataGrid-columnHeaderTitleContainer {
    padding: 0;
  }

  .MuiDataGrid-root .MuiDataGrid-columnHeader,
  .MuiDataGrid-root .MuiDataGrid-cell {
    padding: 0 0 0 5px;
  }
`;
